import type { GuardResult } from '@glyphic/shared-core'
import { formatImageGenMetaLine } from '@glyphic/shared-core'

import type { ClientUiPublic } from '../api/platformConfig'
import { requireCatalogPlatformId } from '../constants/aiPlatforms'
import {
  effectiveImageSizes,
  maxImagesPerRequestForModel,
  resolveApiSizeForOptions,
  resolveClientUiImageModel,
  resolveModelIdForPlatform,
} from './clientUiImageModel'
import { isValidComicPanelAspect, requireValidComicPanelAspect } from './comicPanelAspectUi'
import type { ComicPanelAspectPreset, ComicProjectSettings, ComicStoryScalePreset } from './comicDomainTypes'
import { comicReferenceCapableModels } from './comicReferenceModels'
import { runComicGenerationPreflight } from './guards'

export type { ComicPanelAspectPreset, ComicProjectSettings } from './comicDomainTypes'
export {
  COMIC_PANEL_ASPECT_OPTIONS,
  COMIC_PANEL_ASPECT_PLACEHOLDER,
  COMIC_PANEL_ASPECT_VALUES,
  isValidComicPanelAspect,
  panelAspectCssValue,
  panelAspectOptionsFromClientUi,
  panelAspectSelectOptions,
  panelAspectSelectValue,
  panelAspectTailwindClass,
  panelAspectToApiRatio,
  requireValidComicPanelAspect,
} from './comicPanelAspectUi'
export { getProjectPanelAspect, projectHasGeneratedImages } from './comicProjectInspect'

export const COMIC_CANDIDATES_PER_GEN_OPTIONS = [1, 2, 3, 4] as const
export const COMIC_STORY_SCALE_VALUES = ['short', 'medium', 'long', 'epic'] as const

function normalizeStoryScale(raw: unknown): ComicStoryScalePreset {
  const value = String(raw ?? '').trim()
  return COMIC_STORY_SCALE_VALUES.includes(value as ComicStoryScalePreset)
    ? (value as ComicStoryScalePreset)
    : 'medium'
}

export function candidatesPerGenerationOptionsForModel(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
): number[] {
  const cap = Math.min(4, maxImagesPerRequestForModel(clientUi, modelId, platformId))
  return COMIC_CANDIDATES_PER_GEN_OPTIONS.filter((n) => n <= cap)
}

function clampInt(raw: unknown, min: number, max: number, fallback: number): number {
  const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw ?? ''), 10)
  const safeMax = Number.isFinite(max) && max >= min ? max : min
  if (!Number.isFinite(n)) return Math.min(safeMax, Math.max(min, fallback))
  return Math.min(safeMax, Math.max(min, Math.round(n)))
}

/** 将已解析或原始数字钳为 1–4（避免画布出现 NaN 列预览） */
export function clampCandidatesPerGeneration(value: unknown): number {
  const n = typeof value === 'number' ? value : Number.parseInt(String(value ?? ''), 10)
  return Number.isFinite(n) && n >= 1 ? Math.min(4, Math.round(n)) : 1
}

/** 从话次/作品 settings 快照解析「每次生成本页」张数 */
export function effectiveCandidatesPerGeneration(
  raw: unknown,
  clientUi: ClientUiPublic,
): number {
  const n = normalizeComicProjectSettings(
    raw && typeof raw === 'object' ? raw : {},
    clientUi,
  ).candidatesPerGeneration
  return clampCandidatesPerGeneration(n)
}

export function normalizeComicProjectSettings(
  raw: unknown,
  clientUi: ClientUiPublic,
): ComicProjectSettings {
  const o = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {}
  const capableModels = comicReferenceCapableModels(clientUi)
  const catalogModels = capableModels.length > 0 ? capableModels : clientUi.imageModels
  let platformId = typeof o.platformId === 'string' ? o.platformId.trim() : ''
  if (
    capableModels.length > 0 &&
    (!platformId || !capableModels.some((m) => m.platform === platformId))
  ) {
    platformId = capableModels[0]!.platform
  }
  platformId = requireCatalogPlatformId(
    platformId || catalogModels[0]?.platform,
    'comicProjectSettings.platformId',
  )
  const modelId = resolveModelIdForPlatform(catalogModels, platformId, typeof o.modelId === 'string' ? o.modelId : '')
  const sizeOpts = effectiveImageSizes(clientUi, modelId, platformId)
  const apiSize = resolveApiSizeForOptions(sizeOpts, typeof o.apiSize === 'string' ? o.apiSize : '')

  const genCap = Math.max(1, Math.min(4, maxImagesPerRequestForModel(clientUi, modelId, platformId)))
  const defaultGen = genCap >= 2 ? Math.min(2, genCap) : 1

  return {
    apiSize,
    platformId,
    modelId,
    storyScale: normalizeStoryScale(o.storyScale),
    maxPagesPerEpisode: clampInt(o.maxPagesPerEpisode, 1, 80, 8),
    candidatesPerGeneration: clampInt(o.candidatesPerGeneration, 1, genCap, defaultGen),
    maxCandidatesStored: clampInt(o.maxCandidatesStored, 4, 24, 12),
  }
}

/** 按画册比例从模型尺寸列表中推荐一项（仅用于用户主动改比例时的辅助，非系统默认配置） */
export function suggestApiSizeForPanelAspect(
  sizeOpts: { value: string; previewWidth: number; previewHeight: number }[],
  aspect: ComicPanelAspectPreset,
): string {
  if (sizeOpts.length === 0) return ''
  const [rw, rh] = aspect.split(':').map(Number)
  const target = rw / rh
  let best = sizeOpts[0]!
  let bestDiff = Number.POSITIVE_INFINITY
  for (const opt of sizeOpts) {
    const r = opt.previewWidth / opt.previewHeight
    const diff = Math.abs(Math.log(r / target))
    if (diff < bestDiff) {
      bestDiff = diff
      best = opt
    }
  }
  return best.value
}

function gcd(a: number, b: number): number {
  let x = Math.abs(Math.round(a))
  let y = Math.abs(Math.round(b))
  while (y !== 0) {
    const next = x % y
    x = y
    y = next
  }
  return Math.max(1, x)
}

function sizeOptionAspectRatio(opt: { previewWidth: number; previewHeight: number }): string | null {
  const w = Math.max(0, Math.round(opt.previewWidth))
  const h = Math.max(0, Math.round(opt.previewHeight))
  if (w <= 0 || h <= 0) return null
  const d = gcd(w, h)
  return `${w / d}:${h / d}`
}

function explicitComicSizeOptions(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
) {
  return effectiveImageSizes(clientUi, modelId, platformId).filter(
    (opt) => opt.value.trim().toLowerCase() !== 'auto',
  )
}

export function modelSupportsPanelAspect(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
  panelAspect: ComicPanelAspectPreset,
): boolean {
  if (!isValidComicPanelAspect(panelAspect)) return false
  const model = resolveClientUiImageModel(clientUi, modelId, platformId)
  if (!model) return false
  if (model.aspectRatioOptions?.length) {
    return model.aspectRatioOptions.some((opt) => opt.value === panelAspect)
  }
  return explicitComicSizeOptions(clientUi, modelId, platformId).some(
    (opt) => sizeOptionAspectRatio(opt) === panelAspect,
  )
}

export function sizeOptionsForPanelAspect(
  clientUi: ClientUiPublic,
  modelId: string,
  platformId: string,
  panelAspect: ComicPanelAspectPreset,
) {
  const model = resolveClientUiImageModel(clientUi, modelId, platformId)
  const explicit = explicitComicSizeOptions(clientUi, modelId, platformId)
  if (!isValidComicPanelAspect(panelAspect)) return explicit
  if (model?.aspectRatioOptions?.length) {
    return explicit
  }
  return explicit.filter((opt) => sizeOptionAspectRatio(opt) === panelAspect)
}

function firstModelSupportingPanelAspect(
  clientUi: ClientUiPublic,
  panelAspect: ComicPanelAspectPreset,
  preferredPlatformId?: string,
) {
  const models = comicReferenceCapableModels(clientUi)
  const preferred = preferredPlatformId?.trim()
  if (preferred) {
    const hit = models.find(
      (model) =>
        model.platform === preferred &&
        modelSupportsPanelAspect(clientUi, model.id, model.platform, panelAspect),
    )
    if (hit) return hit
  }
  return models.find((model) =>
    modelSupportsPanelAspect(clientUi, model.id, model.platform, panelAspect),
  )
}

export function syncComicProjectSettingsToPanelAspect(
  settings: ComicProjectSettings,
  clientUi: ClientUiPublic,
  panelAspect: ComicPanelAspectPreset,
): ComicProjectSettings {
  const normalized = normalizeComicProjectSettings(settings, clientUi)
  if (!isValidComicPanelAspect(panelAspect)) {
    return normalized
  }
  let nextPlatformId = normalized.platformId
  let nextModelId = normalized.modelId
  if (!modelSupportsPanelAspect(clientUi, nextModelId, nextPlatformId, panelAspect)) {
    const fallback = firstModelSupportingPanelAspect(clientUi, panelAspect, nextPlatformId)
    if (fallback) {
      nextPlatformId = fallback.platform
      nextModelId = fallback.id
    }
  }
  const nextSizeOptions = sizeOptionsForPanelAspect(clientUi, nextModelId, nextPlatformId, panelAspect)
  const nextApiSize = resolveApiSizeForOptions(nextSizeOptions, normalized.apiSize)
  return normalizeComicProjectSettings(
    {
      ...normalized,
      platformId: nextPlatformId,
      modelId: nextModelId,
      apiSize: nextApiSize,
    },
    clientUi,
  )
}

export function buildDefaultComicProjectSettings(clientUi: ClientUiPublic): ComicProjectSettings {
  return normalizeComicProjectSettings({}, clientUi)
}

export type ComicProjectSettingsValidation = GuardResult

/** 生图前 / 保存设置：委托统一 Guard 预检 */
export function validateComicProjectSettings(
  settings: ComicProjectSettings,
  clientUi: ClientUiPublic,
): ComicProjectSettingsValidation {
  return runComicGenerationPreflight(settings, clientUi)
}

export function validateComicProjectSettingsForPanelAspect(
  settings: ComicProjectSettings,
  clientUi: ClientUiPublic,
  panelAspect: ComicPanelAspectPreset,
): ComicProjectSettingsValidation {
  const base = validateComicProjectSettings(settings, clientUi)
  if (!base.ok) return base
  const aspect = requireValidComicPanelAspect(panelAspect)
  if (!aspect.ok) return aspect
  if (!modelSupportsPanelAspect(clientUi, settings.modelId, settings.platformId, panelAspect)) {
    return {
      ok: false,
      message: `当前模型不支持画册比例 ${panelAspect}，请更换模型或调整单页比例`,
    }
  }
  const trimmedSize = settings.apiSize.trim().toLowerCase()
  if (trimmedSize === 'auto') {
    return {
      ok: false,
      message: '漫画项目不支持自适应尺寸，请选择明确输出尺寸',
    }
  }
  const allowedSizes = sizeOptionsForPanelAspect(clientUi, settings.modelId, settings.platformId, panelAspect)
  if (allowedSizes.length === 0) {
    return {
      ok: false,
      message: `当前模型没有与画册比例 ${panelAspect} 对齐的明确尺寸`,
    }
  }
  if (!allowedSizes.some((opt) => opt.value === settings.apiSize)) {
    return {
      ok: false,
      message: `当前输出尺寸与画册比例 ${panelAspect} 不一致，请重新选择尺寸`,
    }
  }
  return { ok: true }
}

export type ComicSettingsChangeWarning = {
  field: keyof ComicProjectSettings
  message: string
}

/** 有成图时修改关键参数：提示但不阻塞 */
export function warningsForSettingsChange(
  prev: ComicProjectSettings,
  next: ComicProjectSettings,
  hasImages: boolean,
): ComicSettingsChangeWarning[] {
  if (!hasImages) return []
  const out: ComicSettingsChangeWarning[] = []
  if (prev.apiSize !== next.apiSize) {
    out.push({
      field: 'apiSize',
      message: '已有成图，修改输出尺寸后新生成的格子将与旧图分辨率不一致。',
    })
  }
  if (prev.platformId !== next.platformId || prev.modelId !== next.modelId) {
    out.push({
      field: 'modelId',
      message: '已有成图，更换模型/平台后画风可能与已生成格子不统一。',
    })
  }
  return out
}

export function settingsSummaryLabel(settings: ComicProjectSettings, clientUi: ClientUiPublic): string {
  const model =
    clientUi.imageModels.find((m) => m.id === settings.modelId && m.platform === settings.platformId)
      ?.label ?? settings.modelId
  const sizeLabel =
    effectiveImageSizes(clientUi, settings.modelId, settings.platformId).find(
      (o) => o.value === settings.apiSize,
    )?.label ?? settings.apiSize
  return formatImageGenMetaLine(
    {
      platform: settings.platformId,
      modelLabel: model,
      modelId: settings.modelId,
      aspectLabel: sizeLabel,
      params: {
        size: settings.apiSize,
        n: settings.candidatesPerGeneration,
      },
      itemCount: settings.candidatesPerGeneration,
    },
    ' · ',
  )
}

export function resolveModelForSettings(clientUi: ClientUiPublic, settings: ComicProjectSettings) {
  return resolveClientUiImageModel(clientUi, settings.modelId, settings.platformId)
}
